import React from 'react';
import { useTranslation } from 'react-i18next';

export interface CropTypeOption {
  id: string;
  name: string;
}

export interface RegionOption {
  id: string;
  name: string;
}

export interface CropRow {
  cropTypeId: string;
  regionId: string;
}

export interface CustomerCropsTableProps {
  value: CropRow[];
  cropTypes: CropTypeOption[];
  regions: RegionOption[];
  onChange: (_rows: CropRow[]) => void;
  error?: string;
  disabled?: boolean;
}

const selectBase =
  'w-full px-3 py-2 border border-gray-300 rounded-sm font-body text-sm bg-white text-gray-900 outline-none transition-agrobook duration-agrobook focus:border-green-600 focus:ring-4 focus:ring-green-600/15 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

function hasDuplicate(rows: CropRow[], index: number): boolean {
  const row = rows[index];
  if (!row.cropTypeId || !row.regionId) return false;
  return rows.some(
    (r, i) => i !== index && r.cropTypeId === row.cropTypeId && r.regionId === row.regionId,
  );
}

export const CustomerCropsTable: React.FC<CustomerCropsTableProps> = ({
  value,
  cropTypes,
  regions,
  onChange,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();

  function handleFieldChange(index: number, field: keyof CropRow, newValue: string) {
    const updated = value.map((row, i) => (i === index ? { ...row, [field]: newValue } : row));
    onChange(updated);
  }

  function handleAdd() {
    onChange([...value, { cropTypeId: '', regionId: '' }]);
  }

  function handleRemove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Section label */}
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xs font-semibold text-gray-700 tracking-wider">
          {t('customers.crops')}
        </span>
        <span className="text-3xs text-gray-400 tracking-wide">{t('customers.cropsRequiredHint')}</span>
      </div>

      {/* Table */}
      {value.length > 0 && (
        <div className="overflow-x-auto rounded-sm border border-gray-200">
          <table className="w-full border-collapse text-sm font-body">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="ps-3 pe-2 py-2 text-start text-3xs font-semibold text-gray-500 tracking-wider w-1/2">
                  {t('customers.cropTypeCol')}
                </th>
                <th className="ps-2 pe-2 py-2 text-start text-3xs font-semibold text-gray-500 tracking-wider w-1/2">
                  {t('customers.regionCol')}
                </th>
                <th className="ps-2 pe-3 py-2 w-10" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {value.map((row, index) => {
                const isDuplicate = hasDuplicate(value, index);
                const isIncomplete =
                  (row.cropTypeId && !row.regionId) || (!row.cropTypeId && row.regionId);
                const showRowError = isDuplicate || isIncomplete;
                const rowErrorMsg = isDuplicate
                  ? t('validation.duplicateCropRow')
                  : t('validation.selectBothCropAndRegion');

                return (
                  <React.Fragment key={index}>
                    <tr
                      className={`border-b border-gray-100 last:border-b-0 ${showRowError ? 'bg-red-50' : 'bg-white'}`}
                    >
                      <td className="ps-3 pe-2 py-2">
                        <select
                          aria-label={t('customers.cropTypeRowAria', { num: index + 1 })}
                          value={row.cropTypeId}
                          onChange={e => handleFieldChange(index, 'cropTypeId', e.target.value)}
                          disabled={disabled}
                          className={`${selectBase} ${showRowError ? 'border-red-400 focus:border-red-500 focus:ring-red-500/15' : ''}`}
                        >
                          <option value="">{t('customers.selectCropTypePlaceholder')}</option>
                          {cropTypes.map(ct => (
                            <option key={ct.id} value={ct.id}>
                              {ct.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="ps-2 pe-2 py-2">
                        <select
                          aria-label={t('customers.regionRowAria', { num: index + 1 })}
                          value={row.regionId}
                          onChange={e => handleFieldChange(index, 'regionId', e.target.value)}
                          disabled={disabled}
                          className={`${selectBase} ${showRowError ? 'border-red-400 focus:border-red-500 focus:ring-red-500/15' : ''}`}
                        >
                          <option value="">{t('customers.selectRegionPlaceholder')}</option>
                          {regions.map(r => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="ps-2 pe-3 py-2 text-end">
                        <button
                          type="button"
                          aria-label={t('customers.removeRowAria', { num: index + 1 })}
                          onClick={() => handleRemove(index)}
                          disabled={disabled}
                          className="text-gray-400 hover:text-red-500 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                    {showRowError && (
                      <tr className="bg-red-50">
                        <td colSpan={3} className="ps-3 pe-3 pb-2 text-xs text-red-600">
                          {rowErrorMsg}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add row button */}
      <button
        type="button"
        onClick={handleAdd}
        disabled={disabled}
        className="self-start text-sm font-body font-medium text-green-700 hover:text-green-900 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t('customers.addCrop')}
      </button>

      {/* Table-level validation error (at-least-one) */}
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};
